import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { Alert, Button, Card, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { CodPanel } from '../cod/CodPanel.js';
import { CashSection } from '../payments/CashSection.js';
import { ShipmentsSection } from '../shipping/ShipmentsSection.js';
import { Loading } from '../../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  type ListQuery,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
} from '../../grid.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from '../catalog/shared.js';
import { fromBasisPoints } from '../checkout/shared.js';

type Summary = Schemas['OrderSummary'];
type Order = Schemas['Order'];
type HistoryItem = Schemas['OrderHistory']['items'][number];
type Status = Order['status'];
type PaymentStatus = Order['paymentStatus'];
type ActionName = Schemas['OrderActionRequest']['action'];
type ReturnRequest = Schemas['ReturnRequest'];

const STATUSES: readonly Status[] = [
  'placed',
  'confirmed',
  'shipped',
  'delivered',
  'cancelled',
  'returned',
];
const PAYMENT_STATUSES: readonly PaymentStatus[] = ['pending', 'paid', 'refunded'];

/** Actions the merchant can take from each status (mirrors the API rules). */
function availableActions(order: Order): ActionName[] {
  const byStatus: Record<Status, ActionName[]> = {
    placed: ['confirm', 'cancel'],
    confirmed: ['ship', 'cancel'],
    shipped: ['deliver', 'return'],
    delivered: ['return'],
    cancelled: [],
    returned: [],
  };
  // Parcels still out: the order is delivered from the parcels (see the shipments section).
  const parcelsOut = order.shipments.some((shipment) => shipment.status === 'shipped');
  return [
    ...byStatus[order.status].filter((action) => !(action === 'deliver' && parcelsOut)),
    ...(order.paymentStatus === 'pending' &&
    order.status !== 'cancelled' &&
    order.status !== 'returned'
      ? (['mark-paid'] as const)
      : []),
    ...(order.paymentStatus === 'paid' ? (['refund'] as const) : []),
  ];
}

function orderError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError) {
    switch (failure.code) {
      case 'invalid-transition':
        return t('admin.order.error.invalid-transition');
      case 'version-conflict':
        return t('admin.order.error.version-conflict');
      case 'invoice-settings-missing':
        return t('admin.order.error.invoice-settings-missing');
      case 'conflict':
        return t('admin.order.error.invoice-exists');
      default:
        break;
    }
  }
  return errorMessage(t, failure);
}

/** Orders of the store, newest first, filtered by status and payment. */
type OrdersQuery = ListQuery<'/admin-api/v1/stores/{storeId}/orders'>;
const PAYMENT_METHODS = ['cash_on_delivery', 'bank_transfer'] as const;
const DELIVERY_METHODS = ['shipping', 'pickup'] as const;

export function OrdersPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatDateTime, formatPrice } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const grid = useGridState({
    sort: '-number',
    sortable: ['number', 'customer', 'total'],
    filters: { q: '', status: '', payment: '', method: '', delivery: '', from: '', to: '' },
  });
  const rowLink = useRowLink();
  const { filters } = grid;
  const key = JSON.stringify([storeId, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error } = usePagedData<Summary>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/orders', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<OrdersQuery['sort']>,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.status === '' ? {} : { status: filters.status as Status }),
              ...(filters.payment === ''
                ? {}
                : { paymentStatus: filters.payment as PaymentStatus }),
              ...(filters.method === ''
                ? {}
                : { paymentMethod: filters.method as (typeof PAYMENT_METHODS)[number] }),
              ...(filters.delivery === ''
                ? {}
                : { deliveryMethod: filters.delivery as (typeof DELIVERY_METHODS)[number] }),
              ...(filters.from === '' ? {} : { from: filters.from }),
              ...(filters.to === '' ? {} : { to: filters.to }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  const header = (field: string, label: string, firstDescending = false, numeric = false) => (
    <SortHeader
      field={field}
      label={label}
      sort={grid.sort}
      onSort={grid.setSort}
      firstDescending={firstDescending}
      numeric={numeric}
    />
  );
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.order.title')}</h1>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.order.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.order.status')}
          value={filters.status}
          onChange={(event) => {
            grid.setFilter('status', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.order.filter.allStatuses') },
            ...STATUSES.map((value) => ({ value, label: t(`admin.order.status.${value}`) })),
          ]}
        />
        <SelectField
          label={t('admin.order.paymentStatus')}
          value={filters.payment}
          onChange={(event) => {
            grid.setFilter('payment', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.order.filter.allPayments') },
            ...PAYMENT_STATUSES.map((value) => ({
              value,
              label: t(`admin.order.paymentStatus.${value}`),
            })),
          ]}
        />
        <SelectField
          label={t('admin.order.paymentMethod')}
          value={filters.method}
          onChange={(event) => {
            grid.setFilter('method', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.order.filter.allPaymentMethods') },
            ...PAYMENT_METHODS.map((value) => ({
              value,
              label: t(`admin.order.payment.${value}`),
            })),
          ]}
        />
        <SelectField
          label={t('admin.order.deliveryMethod')}
          value={filters.delivery}
          onChange={(event) => {
            grid.setFilter('delivery', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.order.filter.allDeliveryMethods') },
            ...DELIVERY_METHODS.map((value) => ({
              value,
              label: t(`admin.order.delivery.${value}`),
            })),
          ]}
        />
        <TextField
          type="date"
          label={t('admin.grid.from')}
          value={filters.from}
          max={filters.to === '' ? undefined : filters.to}
          onChange={(event) => {
            grid.setFilter('from', event.target.value);
          }}
        />
        <TextField
          type="date"
          label={t('admin.grid.to')}
          value={filters.to}
          min={filters.from === '' ? undefined : filters.from}
          onChange={(event) => {
            grid.setFilter('to', event.target.value);
          }}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.order.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  {header('number', t('admin.order.column.number'), true)}
                  <th scope="col">{t('admin.order.column.date')}</th>
                  {header('customer', t('admin.order.column.customer'))}
                  {header('total', t('admin.order.column.total'), true, true)}
                  <th scope="col">{t('admin.order.status')}</th>
                  <th scope="col">{t('admin.order.column.payment')}</th>
                  <th scope="col">{t('admin.order.column.delivery')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((order) => (
                  <tr
                    key={order.orderId}
                    {...rowLink(`/stores/${storeId}/orders/${order.orderId}`)}
                  >
                    <td>
                      <Link
                        className="admin-cell-link"
                        to={`/stores/${storeId}/orders/${order.orderId}`}
                        aria-label={t('admin.order.open', { number: String(order.number) })}
                      >
                        #{order.number}
                      </Link>
                    </td>
                    <td className="admin-nowrap">{formatDateTime(order.createdAt)}</td>
                    <td>
                      <span dir="auto" className="admin-cell-strong">
                        {order.customerName ?? t('admin.privacy.erasedCustomer')}
                      </span>
                      <br />
                      <span className="mrigl-ltr mrigl-muted">{order.customerPhone}</span>
                    </td>
                    <td className="admin-num admin-nowrap">{formatPrice(order.total.minor)}</td>
                    <td>
                      <span className={`admin-status admin-status--${order.status}`}>
                        {t(`admin.order.status.${order.status}`)}
                      </span>
                    </td>
                    <td>
                      <span className="admin-cell-strong">
                        {t(`admin.order.payment.${order.paymentMethod}`)}
                      </span>
                      <br />
                      <span className={`admin-pay admin-pay--${order.paymentStatus}`}>
                        {t(`admin.order.paymentStatus.${order.paymentStatus}`)}
                      </span>
                    </td>
                    <td>{t(`admin.order.delivery.${order.deliveryMethod}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}

/** One order: customer and delivery, items and totals, actions, invoice and history. */
export function OrderPage() {
  const { storeId = '', orderId = '' } = useParams();
  const { t } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [order, setOrder] = useState<Order | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    const result = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}/history', {
        params: { path: { storeId, orderId } },
      }),
    );
    setHistory(result.items);
  }, [storeId, orderId]);

  useEffect(() => {
    void Promise.all([
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}', {
          params: { path: { storeId, orderId } },
        }),
      ).then(setOrder),
      loadHistory(),
    ]).catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [storeId, orderId, t, loadHistory]);

  const refresh = async (next: Order) => {
    setOrder(next);
    await loadHistory().catch(() => undefined);
  };

  const failed = storeError ?? error;
  if (failed !== null) return <Alert tone="error">{failed}</Alert>;
  if (order === null || store === null) return <Loading />;
  return (
    <OrderView
      key={order.version}
      store={store}
      order={order}
      history={history}
      onChange={refresh}
      onNote={() => loadHistory()}
    />
  );
}

function OrderView({
  store,
  order,
  history,
  onChange,
  onNote,
}: {
  readonly store: StoreDetails;
  readonly order: Order;
  readonly history: readonly HistoryItem[];
  readonly onChange: (order: Order) => Promise<void>;
  readonly onNote: () => Promise<void>;
}) {
  const { t, formatDateTime, formatPrice } = useI18n();
  const storeId = store.storeId;
  const canManage = store.permissions.includes('orders.manage');
  const number = String(order.number);
  const address = order.shippingAddress;
  const totals = order.totals;

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}/orders`}>{t('admin.order.title')}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.order.number', { number })}</h1>
      <p className="mrigl-muted">
        {t('admin.order.placedAt', { date: formatDateTime(order.createdAt) })}
      </p>
      {order.anonymisedAt === null ? null : (
        <p className="mrigl-muted">
          {t('admin.privacy.erasedOn', { date: formatDateTime(order.anonymisedAt) })}
        </p>
      )}
      <p>
        <span className={`admin-status admin-status--${order.status}`}>
          {t(`admin.order.status.${order.status}`)}
        </span>{' '}
        <span className="admin-status">
          {t(`admin.order.paymentStatus.${order.paymentStatus}`)}
        </span>
      </p>

      {canManage ? <OrderActions storeId={storeId} order={order} onChange={onChange} /> : null}
      <ReturnRequestSection storeId={storeId} orderId={order.orderId} canManage={canManage} />

      <Card labelledBy="order-customer">
        <h2 id="order-customer" className="mrigl-subheading">
          {t('admin.order.customer')}
        </h2>
        <dl className="admin-details">
          <dt>{t('admin.order.column.customer')}</dt>
          <dd dir="auto">{order.customer.name ?? t('admin.privacy.erasedCustomer')}</dd>
          {order.customer.phone === null ? null : (
            <>
              <dt>{t('admin.order.phone')}</dt>
              <dd className="mrigl-ltr">
                <a href={`tel:${order.customer.phone}`}>{order.customer.phone}</a>
              </dd>
            </>
          )}
          {order.customer.email === null ? null : (
            <>
              <dt>{t('admin.order.email')}</dt>
              <dd className="mrigl-ltr">{order.customer.email}</dd>
            </>
          )}
          <dt>{t('admin.order.column.delivery')}</dt>
          <dd>{t(`admin.order.delivery.${order.deliveryMethod}`)}</dd>
          {address === null ? null : (
            <>
              <dt>{t('admin.order.address')}</dt>
              <dd dir="auto">
                {address.line1}
                {address.line2 === null ? null : (
                  <>
                    <br />
                    {address.line2}
                  </>
                )}
                <br />
                {[address.postalCode, address.city].filter(Boolean).join(' ')}
                <br />
                {address.governorateName}
              </dd>
            </>
          )}
          {order.trackingNote === null ? null : (
            <>
              <dt>{t('admin.order.trackingNote')}</dt>
              <dd dir="auto">{order.trackingNote}</dd>
            </>
          )}
          <dt>{t('admin.order.column.payment')}</dt>
          <dd>{t(`admin.order.payment.${order.paymentMethod}`)}</dd>
          {order.paymentInstructions === null ? null : (
            <>
              <dt>{t('admin.order.instructions')}</dt>
              <dd dir="auto">{order.paymentInstructions}</dd>
            </>
          )}
          {order.note === null ? null : (
            <>
              <dt>{t('admin.order.note')}</dt>
              <dd dir="auto">{order.note}</dd>
            </>
          )}
        </dl>
      </Card>

      <Card labelledBy="order-items">
        <h2 id="order-items" className="mrigl-subheading">
          {t('admin.order.items')}
        </h2>
        <div className="admin-table-wrap">
          <table className="mrigl-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.order.column.item')}</th>
                <th scope="col">{t('admin.product.sku')}</th>
                <th scope="col">{t('admin.order.column.unitPrice')}</th>
                <th scope="col">{t('admin.order.column.quantity')}</th>
                <th scope="col">{t('admin.order.column.total')}</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => (
                <tr key={line.variantId}>
                  <td dir="auto">
                    {line.variantLabel === null
                      ? line.title
                      : `${line.title} — ${line.variantLabel}`}
                  </td>
                  <td className="mrigl-ltr">{line.sku ?? '—'}</td>
                  <td>{formatPrice(line.unitPrice.minor)}</td>
                  <td>{line.quantity}</td>
                  <td>{formatPrice(line.total.minor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <dl className="admin-details">
          <dt>{t('admin.order.subtotal')}</dt>
          <dd>{formatPrice(totals.subtotal.minor)}</dd>
          {totals.discount.minor === '0' ? null : (
            <>
              <dt>{t('admin.order.discount', { code: order.discountCode ?? '' })}</dt>
              <dd>−{formatPrice(totals.discount.minor)}</dd>
            </>
          )}
          {order.deliveryMethod === 'shipping' ? (
            <>
              <dt>{t('admin.order.shipping')}</dt>
              <dd>{formatPrice(totals.shipping.minor)}</dd>
            </>
          ) : null}
          {order.taxBreakdown.length === 0 ? null : (
            <>
              <dt>
                {totals.pricesIncludeTax ? t('admin.order.taxIncluded') : t('admin.order.tax')}
              </dt>
              <dd>{formatPrice(totals.tax.minor)}</dd>
            </>
          )}
          {totals.fee.minor === '0' ? null : (
            <>
              <dt>{t('admin.order.fee', { label: order.feeLabel ?? '' })}</dt>
              <dd>{formatPrice(totals.fee.minor)}</dd>
            </>
          )}
          <dt>
            <strong>{t('admin.order.total')}</strong>
          </dt>
          <dd>
            <strong>{formatPrice(totals.total.minor)}</strong>
          </dd>
        </dl>
        {order.taxBreakdown.length === 0 ? null : (
          <ul className="mrigl-list mrigl-muted">
            {order.taxBreakdown.map((group) => (
              <li key={group.rateBp}>
                {t('admin.order.taxLine', {
                  rate: fromBasisPoints(group.rateBp),
                  net: formatPrice(group.net.minor),
                  tax: formatPrice(group.tax.minor),
                })}
              </li>
            ))}
          </ul>
        )}
        <p className="mrigl-muted">{t('admin.order.fiscalNote')}</p>
      </Card>

      {order.paymentMethod === 'cash_on_delivery' && order.customer.phone !== null ? (
        <CodPanel storeId={storeId} order={order} canManage={canManage} onChange={onChange} />
      ) : null}

      {order.status === 'placed' || order.status === 'cancelled' ? null : (
        <ShipmentsSection
          storeId={storeId}
          order={order}
          canManage={canManage}
          onChange={onChange}
        />
      )}

      {order.paymentMethod === 'cash_on_delivery' && order.status !== 'placed' ? (
        <CashSection storeId={storeId} order={order} canManage={canManage} onChange={onChange} />
      ) : null}

      <InvoiceSection storeId={storeId} order={order} canManage={canManage} onChange={onChange} />

      <Card labelledBy="order-history">
        <h2 id="order-history" className="mrigl-subheading">
          {t('admin.order.history')}
        </h2>
        <ol className="admin-history">
          {history.map((item) => (
            <li key={item.eventId}>
              <span className="mrigl-muted">{formatDateTime(item.createdAt)}</span>{' '}
              {historyText(t, item)}
              {item.source === null ? null : (
                <>
                  {' — '}
                  {item.source === 'customer'
                    ? t('admin.order.history.byCustomer')
                    : t('admin.order.history.automatic')}
                </>
              )}
              {item.note === null ? null : (
                <>
                  {' — '}
                  <span dir="auto">{item.note}</span>
                </>
              )}
              {item.restocked.length === 0 ? null : (
                <>
                  {' — '}
                  {t('admin.order.history.restocked', {
                    count: item.restocked.reduce((sum, line) => sum + line.quantity, 0),
                  })}
                </>
              )}
            </li>
          ))}
        </ol>
        {canManage ? <NoteForm storeId={storeId} orderId={order.orderId} onAdded={onNote} /> : null}
      </Card>
    </div>
  );
}

function historyText(t: I18n['t'], item: HistoryItem): string {
  const status = (value: string | null) =>
    value === null ? '' : t(`admin.order.status.${value}` as MessageKey);
  const payment = (value: string | null) =>
    value === null ? '' : t(`admin.order.paymentStatus.${value}` as MessageKey);
  switch (item.kind) {
    case 'placed':
      return t('admin.order.history.placed');
    case 'status':
      return t('admin.order.history.status', { from: status(item.from), to: status(item.to) });
    case 'payment':
      return t('admin.order.history.payment', { from: payment(item.from), to: payment(item.to) });
    case 'invoice':
      return t('admin.order.history.invoice', { number: item.to ?? '' });
    case 'shipment':
      return t('admin.order.history.shipment', {
        number: String(item.shipmentNumber ?? ''),
        status: item.to === null ? '' : t(`admin.shipment.status.${item.to}` as MessageKey),
      });
    case 'call':
      return t('admin.cod.history.call', {
        outcome: item.to === null ? '' : t(`admin.cod.outcome.${item.to}` as MessageKey),
      });
    default:
      return t('admin.order.history.note');
  }
}

/** Buttons for the possible changes; the chosen one opens its form. */
function OrderActions({
  storeId,
  order,
  onChange,
}: {
  readonly storeId: string;
  readonly order: Order;
  readonly onChange: (order: Order) => Promise<void>;
}) {
  const { t } = useI18n();
  const actions = availableActions(order);
  const [chosen, setChosen] = useState<ActionName | null>(null);
  const [note, setNote] = useState('');
  const [tracking, setTracking] = useState('');
  const [restock, setRestock] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (chosen === null) return;
    setBusy(true);
    setError(null);
    const trimmed = note.trim() === '' ? null : note.trim();
    const body: Schemas['OrderActionRequest'] =
      chosen === 'ship'
        ? {
            action: 'ship',
            version: order.version,
            note: trimmed,
            trackingNote: tracking.trim() === '' ? null : tracking.trim(),
          }
        : chosen === 'return'
          ? {
              action: 'return',
              version: order.version,
              note: trimmed,
              restock: Object.entries(restock)
                .map(([variantId, value]) => ({ variantId, quantity: Number(value) }))
                .filter((line) => Number.isInteger(line.quantity) && line.quantity > 0),
            }
          : { action: chosen, version: order.version, note: trimmed };
    try {
      const next = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/actions', {
          params: { path: { storeId, orderId: order.orderId } },
          body,
        }),
      );
      await onChange(next);
    } catch (failure) {
      setError(orderError(t, failure));
      setBusy(false);
    }
  }

  return (
    <Card labelledBy="order-actions">
      <h2 id="order-actions" className="mrigl-subheading">
        {t('admin.order.actions')}
      </h2>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {actions.length === 0 ? (
        <p className="mrigl-muted">{t('admin.order.noActions')}</p>
      ) : (
        <div className="admin-actions">
          {actions.map((action) => (
            <Button
              key={action}
              variant={chosen === action ? 'primary' : 'secondary'}
              aria-pressed={chosen === action}
              onClick={() => {
                setChosen(action);
                setError(null);
              }}
            >
              {t(`admin.order.action.${action}`)}
            </Button>
          ))}
        </div>
      )}
      {chosen === null ? null : (
        <form
          className="mrigl-stack"
          aria-label={t(`admin.order.action.${chosen}`)}
          onSubmit={(event) => void submit(event)}
        >
          {chosen === 'cancel' ? (
            <p className="mrigl-muted">{t('admin.order.cancelHint')}</p>
          ) : null}
          {chosen === 'ship' ? (
            <TextField
              label={t('admin.order.trackingNote')}
              hint={t('admin.order.trackingNoteHint')}
              maxLength={300}
              dir="auto"
              value={tracking}
              onChange={(event) => {
                setTracking(event.target.value);
              }}
            />
          ) : null}
          {chosen === 'return' ? (
            <>
              <p className="mrigl-muted">{t('admin.order.returnHint')}</p>
              <p className="mrigl-muted">{t('admin.order.returnRequest.acceptHint')}</p>
              {order.lines.map((line) => (
                <TextField
                  key={line.variantId}
                  type="number"
                  min={0}
                  max={line.quantity}
                  inputMode="numeric"
                  label={t('admin.order.restockOf', {
                    title:
                      line.variantLabel === null
                        ? line.title
                        : `${line.title} — ${line.variantLabel}`,
                  })}
                  value={restock[line.variantId] ?? '0'}
                  onChange={(event) => {
                    setRestock({ ...restock, [line.variantId]: event.target.value });
                  }}
                />
              ))}
            </>
          ) : null}
          <TextAreaField
            label={t('admin.order.actionNote')}
            maxLength={1000}
            dir="auto"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
          <Button type="submit" busy={busy}>
            {t('admin.order.apply')}
          </Button>
        </form>
      )}
    </Card>
  );
}

function InvoiceSection({
  storeId,
  order,
  canManage,
  onChange,
}: {
  readonly storeId: string;
  readonly order: Order;
  readonly canManage: boolean;
  readonly onChange: (order: Order) => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const [language, setLanguage] = useState<'ar' | 'fr'>(locale === 'fr' ? 'fr' : 'ar');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invoice = order.invoice;

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/invoice', {
          params: { path: { storeId, orderId: order.orderId } },
          body: { locale: language },
        }),
      );
      const next = await unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}', {
          params: { path: { storeId, orderId: order.orderId } },
        }),
      );
      await onChange(next);
    } catch (failure) {
      setError(orderError(t, failure));
      setBusy(false);
    }
  }

  return (
    <Card labelledBy="order-invoice">
      <h2 id="order-invoice" className="mrigl-subheading">
        {t('admin.order.invoice')}
      </h2>
      {error === null ? null : (
        <Alert tone="error">
          {error}{' '}
          {error === t('admin.order.error.invoice-settings-missing') ? (
            <Link to={`/stores/${storeId}/invoicing`}>{t('admin.invoice.title')}</Link>
          ) : null}
        </Alert>
      )}
      {invoice === null ? (
        <>
          <p className="mrigl-muted">{t('admin.order.invoiceNone')}</p>
          {canManage && order.status !== 'cancelled' ? (
            <div className="admin-actions">
              <SelectField
                label={t('admin.order.invoiceLanguage')}
                value={language}
                onChange={(event) => {
                  setLanguage(event.target.value as 'ar' | 'fr');
                }}
                options={[
                  { value: 'ar', label: t('admin.language.ar'), lang: 'ar' },
                  { value: 'fr', label: t('admin.language.fr'), lang: 'fr' },
                ]}
              />
              <Button busy={busy} onClick={() => void issue()}>
                {t('admin.order.issueInvoice')}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <p>
          <a href={`/admin-api/v1/stores/${storeId}/invoices/${invoice.invoiceId}/pdf`} download>
            {t('admin.order.downloadInvoice', { number: invoice.displayNumber })}
          </a>
        </p>
      )}
    </Card>
  );
}

function NoteForm({
  storeId,
  orderId,
  onAdded,
}: {
  readonly storeId: string;
  readonly orderId: string;
  readonly onAdded: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (note.trim() === '') return;
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/notes', {
          params: { path: { storeId, orderId } },
          body: { note: note.trim() },
        }),
      );
      setNote('');
      setNotice(t('admin.order.noteAdded'));
      await onAdded();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.order.addNote')}
      onSubmit={(event) => void submit(event)}
    >
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <TextAreaField
        label={t('admin.order.noteLabel')}
        maxLength={1000}
        dir="auto"
        required
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      <Button type="submit" variant="secondary" busy={busy}>
        {t('admin.order.addNote')}
      </Button>
    </form>
  );
}

/** The customer's return request. Recording a return accepts it; declining stores a note. */
function ReturnRequestSection({
  storeId,
  orderId,
  canManage,
}: {
  readonly storeId: string;
  readonly orderId: string;
  readonly canManage: boolean;
}) {
  const { t } = useI18n();
  const [request, setRequest] = useState<ReturnRequest | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const body = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/orders/{orderId}/return-request', {
        params: { path: { storeId, orderId } },
      }),
    );
    setRequest(body.request);
  }, [storeId, orderId]);

  useEffect(() => {
    void load().catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [load, t]);

  const decline = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/orders/{orderId}/return-request/decline', {
          params: { path: { storeId, orderId } },
          body: { note: note.trim() },
        }),
      );
      setNote('');
      await load();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card labelledBy="order-return-request">
      <h2 id="order-return-request" className="mrigl-subheading">
        {t('admin.order.returnRequest')}
      </h2>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {request === null ? (
        <p className="mrigl-muted">{t('admin.order.returnRequest.none')}</p>
      ) : (
        <>
          <p>
            <span className="admin-status">
              {t(`admin.order.returnRequest.status.${request.status}`)}
            </span>
          </p>
          {request.message === null ? null : (
            <blockquote className="admin-review-quote" dir="auto">
              {request.message}
            </blockquote>
          )}
          {request.declineNote === null ? null : (
            <p dir="auto">
              {t('admin.order.returnRequest.declineNote')}: {request.declineNote}
            </p>
          )}
          {canManage && request.status === 'requested' ? (
            <form className="mrigl-stack" onSubmit={(event) => void decline(event)}>
              <p className="mrigl-muted">{t('admin.order.returnRequest.acceptHint')}</p>
              <TextAreaField
                label={t('admin.order.returnRequest.declineNote')}
                maxLength={1000}
                dir="auto"
                required
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
              <Button type="submit" variant="secondary" busy={busy}>
                {t('admin.order.returnRequest.decline')}
              </Button>
            </form>
          ) : null}
        </>
      )}
    </Card>
  );
}
